// lista wybranych kategorii (Wise UI - kopia SelectedCategories, osobne style)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import Slider from 'react-slick';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import classnames from 'classnames';
import qs from 'query-string';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { Link } from 'components/controls';
import { useGetHomeCategories } from 'api';
import { useRWD } from 'hooks';

import styles from 'theme/components/wise/WiseSelectedCategories/WiseSelectedCategories.module.scss';

const SLIDES_TO_SHOW_CAP = 6;

const SUPPRESS_LINK_CLICK_AFTER_SWIPE_MS = 450;

export const WiseSelectedCategories = () => {
  const { data: homeCategoriesData } = useGetHomeCategories();
  const items = homeCategoriesData?.items || [];
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const suppressLinkNavUntilRef = useRef(0);
  const { isMobile, isTablet } = useRWD();

  // Efektywne slidesToShow zależnie od viewportu (musi się zgadzać z `responsive` w slick settings,
  // żeby disabled state przycisków odpowiadał faktycznej liczbie widocznych slajdów).
  const effectiveSlidesToShow = useMemo(() => {
    const n = items.length;
    if (n < 1) return 0;
    // Próg <600px = mobile w MUI to sm, ale slick używa breakpoint=600 ⇒ centerMode 1 slajd.
    // useRWD nie ma dedykowanego "isExtraSmall" - używamy window.innerWidth jako bezpieczny fallback.
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
    if (width <= 600) return 1;
    if (isMobile) return Math.min(3, n);
    if (isTablet) return Math.min(SLIDES_TO_SHOW_CAP, n);
    return Math.min(SLIDES_TO_SHOW_CAP, n);
  }, [items.length, isMobile, isTablet]);

  const carouselConfig = useMemo(() => {
    const n = items.length;
    if (n < 1) {
      return null;
    }
    const slidesToShow = Math.min(SLIDES_TO_SHOW_CAP, n);
    return {
      slidesToShow,
      needsNav: n > slidesToShow,
      slickSettings: {
        dots: false,
        infinite: false,
        arrows: false,
        slidesToShow,
        slidesToScroll: 1,
        swipe: true,
        draggable: true,
        touchMove: true,
        swipeToSlide: true,
        touchThreshold: 32,
        // Responsywność: na mobile pokazujemy 1 kafelek + zajawki sąsiednich przez centerMode.
        // Tablety: 3 kafelki bez centerMode.
        responsive: [
          {
            breakpoint: 900,
            settings: {
              slidesToShow: Math.min(3, n),
              centerMode: false
            }
          },
          {
            breakpoint: 600,
            settings: {
              slidesToShow: 1,
              centerMode: true,
              centerPadding: '40px'
            }
          }
        ],
        onSwipe: () => {
          suppressLinkNavUntilRef.current = Date.now() + SUPPRESS_LINK_CLICK_AFTER_SWIPE_MS;
        }
      }
    };
  }, [items.length]);

  useEffect(() => {
    setCurrentSlide(0);
  }, [items.length, effectiveSlidesToShow]);

  const maxSlide = Math.max(0, items.length - effectiveSlidesToShow);
  const canScrollPrev = currentSlide > 0;
  const canScrollNext = currentSlide < maxSlide;

  const onCategoryLinkClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (Date.now() < suppressLinkNavUntilRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Home-components-SelectedCategories-WiseSelectedCategories'
      )}>
      <h2 className={styles.title}>
        <Trans>Wybrane kategorie</Trans>
      </h2>

      {carouselConfig && (() => {
        const needsNav = items.length > effectiveSlidesToShow;
        return (
        <div
          className={classnames(
            styles.categorySlider,
            needsNav && styles.categorySliderWithNav
          )}>
          {needsNav && (
            <>
              <button
                type="button"
                className={classnames(styles.navButton, styles.navPrev)}
                disabled={!canScrollPrev}
                onClick={() => sliderRef.current?.slickPrev()}
                aria-label="Poprzednie kategorie">
                <ChevronLeft />
              </button>
              <button
                type="button"
                className={classnames(styles.navButton, styles.navNext)}
                disabled={!canScrollNext}
                onClick={() => sliderRef.current?.slickNext()}
                aria-label="Następne kategorie">
                <ChevronRight />
              </button>
            </>
          )}

          <div className={styles.slickHost}>
            <Slider
              ref={sliderRef}
              {...carouselConfig.slickSettings}
              afterChange={(index) => setCurrentSlide(index)}>
              {items.map((category) => (
                <div key={category.id} className={styles.slide}>
                  <Link
                    className={styles.item}
                    to={`/products?${qs.stringify({ category_id: category.id })}`}
                    draggable={false}
                    onClick={onCategoryLinkClick}>
                    <div className={styles.border}>
                      <img
                        className={styles.img}
                        src={category.image}
                        alt={`category ${category.name}`}
                        draggable={false}
                      />
                    </div>
                    {category.name && <div className={styles.name}>{category.name}</div>}
                  </Link>
                </div>
              ))}
            </Slider>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default WiseSelectedCategories;
